import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Badge,
} from "reactstrap";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
  PreviewAltCard,
  RSelect,
  TooltipComponent,
} from "../../components/Component"; // Assuming Component.js exports these
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { v4 as uuidv4 } from 'uuid';
import { uploadToS3 } from "../../utils/envConfig"; // Import uploadToS3
import { getAllChannelPartners, createChannelPartner, updateChannelPartner, deleteChannelPartner } from "../../services/channelPartnersApi";
import AddChannelPartnerModal from "./components/AddChannelPartnerModal";
import EditChannelPartnerModal from "./components/EditChannelPartnerModal";
import ChannelPartnerDetailModal from "./components/ChannelPartnerDetailModal"; // Import Detail Modal

const ChannelPartnerListPage = () => {
  const axios = useAxiosPrivate();
  const queryClient = useQueryClient();

  // State Management
  const [searchText, setSearchText] = useState("");
  const [onSearch, setOnSearch] = useState(false); // For search bar visibility
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false); // State for Detail Modal
  const [viewingPartner, setViewingPartner] = useState(null); // State for partner being viewed
  const initialPartnerFormState = {
    name: "",
    type: "",
    managerName: "",
    website: "",
    managerContact: "",
    documentFile: null, // For the selected file object
    document: "", // For the S3 path/URL
    documentType: "", // As requested by user
    active: true, // Default to true for new partners
  };
  const [newPartnerData, setNewPartnerData] = useState(initialPartnerFormState);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [smOption, setSmOption] = useState(false); // For mobile tools toggle
  const [filterType, setFilterType] = useState(""); // For storing selected filter values
  const [filterStatus, setFilterStatus] = useState(""); // For storing selected filter values

  // Data Fetching
  const {
    data: channelPartnersData,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["channelPartners", searchText, filterType, filterStatus, currentPage, itemsPerPage],
    () => getAllChannelPartners(axios, { search: searchText, type: filterType, active: filterStatus, page: currentPage, limit: itemsPerPage }),
    {
      keepPreviousData: true,
      // staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const channelPartners = channelPartnersData?.data || []; // Adjusted for flexibility
  const totalItems = channelPartnersData?.items || 0; // Adjusted for flexibility


  // Event Handlers
  const handleSearchInputChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const toggleSearch = () => setOnSearch(!onSearch);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleOpenDeleteModal = (partner) => {
    setPartnerToDelete(partner);
    setShowDeleteConfirmModal(true);
  };

  const handleCloseDeleteModal = () => {
    setPartnerToDelete(null);
    setShowDeleteConfirmModal(false);
  };

  const handleOpenAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewPartnerData(initialPartnerFormState); // Reset form
  };

  const handleOpenDetailModal = (partner) => {
    setViewingPartner(partner);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setViewingPartner(null);
    setShowDetailModal(false);
  };

  const handleOpenEditModal = (partner) => {
    setEditingPartner(partner);
    setNewPartnerData({
      name: partner.name || "",
      type: partner.type || "",
      managerName: partner.managerName || "",
      website: partner.website || "",
      managerContact: partner.managerContact || "",
      // documents: partner.documents || [], // Keep as per previous state, documentFile handles new uploads
      documentType: partner.documentType || "",
      document: partner.document || "", // This will hold the S3 path of the existing document
      documentFile: null, // Reset file input when opening edit modal
      active: partner.active === undefined ? true : partner.active,
    });
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPartner(null);
    setNewPartnerData(initialPartnerFormState); // Reset form
  };

  const handleNewPartnerDataChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setNewPartnerData(prevState => ({
        ...prevState,
        documentFile: files && files.length > 0 ? files[0] : null,
      }));
    } else {
      setNewPartnerData(prevState => ({
        ...prevState,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNewPartnerTypeChange = (selectedOption) => {
    setNewPartnerData(prevState => ({ ...prevState, type: selectedOption ? selectedOption.value : "" }));
  };


  const handleFilterTypeChange = (selectedOrValue) => {

    if (selectedOrValue && typeof selectedOrValue === 'object' && selectedOrValue.hasOwnProperty('value')) {
      setFilterType(selectedOrValue.value);
    } else if (selectedOrValue === null || selectedOrValue === undefined) {
      setFilterType(""); // Handle clear or undefined
    } else {
      // Assume it's the direct value string if not an object or null/undefined
      setFilterType(String(selectedOrValue));
    }
    setCurrentPage(1);
  };

  const handleFilterStatusChange = (selectedOrValue) => {
    if (selectedOrValue && typeof selectedOrValue === 'object' && selectedOrValue.hasOwnProperty('value')) {
      setFilterStatus(selectedOrValue.value);
    } else if (selectedOrValue === null || selectedOrValue === undefined) {
      setFilterStatus(""); // Handle clear or undefined
    } else {
      // Assume it's the direct value string
      setFilterStatus(String(selectedOrValue));
    }
    setCurrentPage(1);
  };

  // Mutations (Shells)
  const deleteChannelPartnerMutation = useMutation(
    (partnerId) => deleteChannelPartner(axios, partnerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("channelPartners");
        toast.success("Channel Partner deleted successfully.");
        handleCloseDeleteModal();
      },
      onError: (err) => {
        toast.error(`Error deleting partner: ${err?.response?.data?.message || err.message}`);
        handleCloseDeleteModal();
      },
    }
  );

  // Add Channel Partner Mutation
  const addChannelPartnerMutation = useMutation(
    (newData) => createChannelPartner(axios, newData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("channelPartners");
        toast.success("Channel Partner added successfully.");
        handleCloseAddModal();
      },
      onError: (err) => {
        toast.error(`Error adding partner: ${err?.response?.data?.message || err.message}`);
      },
    }
  );

  // Update Channel Partner Mutation
  const updateChannelPartnerMutation = useMutation(
    (updatedData) => updateChannelPartner(axios, editingPartner._id || editingPartner.id, updatedData), // Use _id if available, fallback to id
    {
      onSuccess: () => {
        queryClient.invalidateQueries("channelPartners");
        toast.success("Channel Partner updated successfully.");
        handleCloseEditModal();
      },
      onError: (err) => {
        toast.error(`Error updating partner: ${err?.response?.data?.message || err.message}`);
      },
    }
  );


  const handleDeleteConfirm = () => {
    const partnerId = partnerToDelete?._id || partnerToDelete?.id;
    if (partnerToDelete && partnerId) {
      deleteChannelPartnerMutation.mutate(partnerId);
    } else {
      toast.error("No partner selected for deletion or partner ID is missing.");
      handleCloseDeleteModal();
    }
  };

  const handleAddPartnerSubmit = async () => {
    if (!newPartnerData.name || !newPartnerData.type) {
      toast.error("Partner Name and Type are required.");
      return;
    }

    let partnerDataToSubmit = { ...newPartnerData };
    delete partnerDataToSubmit.documentFile; // Remove file object before sending

    if (newPartnerData.documentFile) {
      const file = newPartnerData.documentFile;
      const fileName = `channel-partners/documents/${uuidv4()}-${file.name}`;
      try {
        toast.info("Uploading document...");
        const s3Path = await uploadToS3(file, fileName, file.type, axios);
        if (s3Path) {
          partnerDataToSubmit.document = fileName; // Store S3 key/path
          toast.success("Document uploaded successfully.");
        } else {
          toast.error("Document upload failed. Please try again.");
          return; // Stop submission if upload fails
        }
      } catch (err) {
        console.error("S3 Upload error:", err);
        toast.error("Error during document upload.");
        return; // Stop submission on error
      }
    }
    addChannelPartnerMutation.mutate(partnerDataToSubmit);
  };

  const handleEditPartnerSubmit = async () => {
    const partnerId = editingPartner?._id || editingPartner?.id;
    if (!editingPartner || !partnerId) {
      toast.error("No partner selected for editing or partner ID is missing.");
      return;
    }
    if (!newPartnerData.name || !newPartnerData.type) {
      toast.error("Partner Name and Type are required.");
      return;
    }

    let partnerDataToSubmit = { ...newPartnerData };
    delete partnerDataToSubmit.documentFile; // Remove file object

    if (newPartnerData.documentFile) {
      const file = newPartnerData.documentFile;
      const fileName = `channel-partners/documents/${uuidv4()}-${file.name}`;
      // Optional: Delete old document from S3 if editingPartner.document exists and is different
      // if (editingPartner.document && editingPartner.document !== fileName) {
      //   await deleteFromS3(editingPartner.document, axios);
      // }
      try {
        toast.info("Uploading new document...");
        const s3Path = await uploadToS3(file, fileName, file.type, axios);
        if (s3Path) {
          partnerDataToSubmit.document = fileName; // Store S3 key/path
          toast.success("New document uploaded successfully.");
        } else {
          toast.error("New document upload failed. Please try again.");
          return;
        }
      } catch (err) {
        console.error("S3 Upload error:", err);
        toast.error("Error during new document upload.");
        return;
      }
    } else {
      // If no new file is selected, ensure the existing document path is preserved
      partnerDataToSubmit.document = editingPartner.document || "";
    }
    updateChannelPartnerMutation.mutate(partnerDataToSubmit);
  };


  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "Broker", label: "Broker" },
    { value: "Agency", label: "Agency" },
    { value: "Developer", label: "Developer" },
    { value: "Referral Partner", label: "Referral Partner" },
    { value: "Aggregator", label: "Aggregator" },
    { value: "Freelancer", label: "Freelancer" },
    { value: "Other", label: "Other" },
  ];

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];



  const getFilterTypeLabel = (value) => {
    if (!value) return "";
    // typeOptions is defined further down, ensure it's accessible
    const option = typeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getFilterStatusLabel = (value) => {
    if (!value) return "";
    // statusOptions is defined further down, ensure it's accessible
    const option = statusOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const clearNameFilter = () => {
    setSearchText("");
    setCurrentPage(1);
  };

  const clearTypeFilter = () => {
    handleFilterTypeChange(null); // This function already handles resetting page and value to ""
  };

  const clearStatusFilter = () => {
    handleFilterStatusChange(null); // This function already handles resetting page and value to ""
  };

  const handleClearAllFilters = () => {
    setSearchText("");
    handleFilterTypeChange(null);
    handleFilterStatusChange(null);
    setCurrentPage(1); // Explicitly ensure page reset
  };

  const hasActiveFilters = !!(searchText || filterType || filterStatus);
  // UI Structure
  return (

    <React.Fragment >
      <Head title="Channel Partners" />
      <Content >
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Channel Partners</BlockTitle>
              <BlockDes className="text-soft">
                {isLoading ? <Spinner size="sm" /> : `You have ${totalItems} channel partners.`}
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${smOption ? "active" : ""}`}
                  onClick={() => setSmOption(!smOption)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: smOption ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                    {/* Removed the single icon button, using the main "Add New" button below */}
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                          <Icon name="filter-alt" className="d-none d-sm-inline"></Icon>
                          <span>Filters</span>
                          <Icon name="chevron-right" className="dd-indc"></Icon>
                        </DropdownToggle>
                        <DropdownMenu end className="filter-wg dropdown-menu-xl" style={{ overflow: 'visible' }}>
                          <div className="dropdown-body dropdown-body-rg">
                            <div className="p-2" style={{ minWidth: "250px" }}>
                              <label htmlFor="filterType" className="form-label">Type</label>
                              <RSelect
                                options={typeOptions.filter(op => op.value !== "")} // Exclude "All Types" for creatable
                                value={filterType ? { label: filterType, value: filterType } : null}
                                onChange={(newValue) => {
                                  if (newValue && newValue.__isNew__) {
                                    // Handle new value creation
                                    handleFilterTypeChange(newValue.value);
                                  } else {
                                    handleFilterTypeChange(newValue);
                                  }
                                }}
                                isClearable
                                isCreatable={true}
                                placeholder="Select or Create Type"
                                formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                classNamePrefix="select"
                                className="react-select-container dropdown-select"
                              />
                            </div>
                            <div className="p-2" style={{ minWidth: "250px" }}>
                              <label htmlFor="filterStatus" className="form-label">Status</label>
                              <RSelect
                                options={statusOptions}
                                value={statusOptions.find(opt => opt.value === filterStatus)}
                                onChange={handleFilterStatusChange}
                                isClearable
                                placeholder="Select Status"
                                classNamePrefix="select"
                                className="react-select-container dropdown-select"
                              />
                            </div>
                          </div>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                    <li>
                      <div className="form-control-wrap " style={{ minWidth: '400px' }}>
                        <div className="form-icon form-icon-right">
                          <Icon name="search" onClick={toggleSearch}></Icon>
                        </div>
                        <Input
                          type="text"
                          className={`form-control ${onSearch ? "expanded" : ""}`}
                          placeholder="Search by name, manager name, contact..."
                          value={searchText}
                          onChange={handleSearchInputChange}
                          onFocus={toggleSearch}
                          onBlur={() => !searchText && toggleSearch()}
                        />
                      </div>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Button color="primary" onClick={handleOpenAddModal}>
                        <Icon name="plus"></Icon>
                        <span>Add New Channel Partner</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        {/* Active Filters Display Section */}
        {hasActiveFilters && (
          <PreviewAltCard className="mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between">
              <div className="d-flex flex-wrap gx-2 gy-2 align-items-center">
                <span className="me-2 fw-bold">Active Filters:</span>
                {searchText && (
                  <Badge color="light" className="me-1 d-flex align-items-center">
                    Name: {searchText}
                    <Icon name="cross-sm" className="ms-1 cursor-pointer" onClick={clearNameFilter} style={{ cursor: 'pointer' }} />
                  </Badge>
                )}
                {filterType && (
                  <Badge color="light" className="me-1 d-flex align-items-center">
                    Type: {getFilterTypeLabel(filterType)}
                    <Icon name="cross-sm" className="ms-1 cursor-pointer" onClick={clearTypeFilter} style={{ cursor: 'pointer' }} />
                  </Badge>
                )}
                {filterStatus && (
                  <Badge color="light" className="me-1 d-flex align-items-center">
                    Status: {getFilterStatusLabel(filterStatus)}
                    <Icon name="cross-sm" className="ms-1 cursor-pointer" onClick={clearStatusFilter} style={{ cursor: 'pointer' }} />
                  </Badge>
                )}
              </div>
              <Button
                color="light"
                outline
                size="sm"
                onClick={handleClearAllFilters}
                className="mt-2 mt-md-0" // Margin top for small screens, none for medium and up
              >
                <Icon name="cross-circle" />
                <span className="ms-1">Clear All Filters</span>
              </Button>
            </div>
          </PreviewAltCard>
        )}

        <Block>
          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead>
                <DataTableRow className={'text-start'}>
                  <span className="sub-text fw-bold">Name</span>
                </DataTableRow>
                <DataTableRow size="mb" className={'text-center'}>
                  <span className="sub-text fw-bold">Type</span>
                </DataTableRow>
                <DataTableRow size="md" className={'text-center'}>
                  <span className="sub-text fw-bold">Manager Name</span>
                </DataTableRow>
                <DataTableRow size="md" className={'text-center'}>
                  <span className="sub-text fw-bold">Status</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-end">
                  <span className="sub-text fw-bold">Actions</span>
                </DataTableRow>
              </DataTableHead>

              {isLoading && (
                <DataTableRow>
                  <DataTableItem colSpan="5" className="text-center">
                    <Spinner color="primary" />
                  </DataTableItem>
                </DataTableRow>
              )}

              {isError && (
                <DataTableRow>
                  <DataTableItem colSpan="5" className="text-center text-danger">
                    Error: {error?.response?.data?.message || error?.message || "Failed to load data."}
                  </DataTableItem>
                </DataTableRow>
              )}

              {!isLoading && !isError && channelPartners.length === 0 && (
                <>
                <DataTableRow>
                  <DataTableItem />
                </DataTableRow>
                <DataTableRow>
                  <DataTableItem />
                </DataTableRow>
                <DataTableRow>
                  <DataTableItem />
                </DataTableRow>
                <DataTableRow>
                  <DataTableItem className="text-center">
                    No channel partners match your search criteria.
                  </DataTableItem>
                </DataTableRow>
                <DataTableRow>
                  <DataTableItem />
                </DataTableRow>
                </>
              )}

              {!isLoading && !isError && channelPartners.map((partner) => (
                <DataTableItem key={partner._id || partner.id}>
                  <DataTableRow className={'text-start'}>
                    <span className="tb-sub">{partner.name}</span>
                  </DataTableRow>
                  <DataTableRow size="mb" className={'text-center'}>
                    <span className="tb-sub">{partner.type}</span>
                  </DataTableRow>
                  <DataTableRow size="md" className={'text-center'}>
                    <span className="tb-sub">{partner.managerName || "N/A"}</span>
                  </DataTableRow>
                  <DataTableRow size="md" className={'text-center'}>
                    <Badge color={partner.active ? "success" : "danger"} className="badge-dot">
                      {partner.active ? "Active" : "Inactive"}
                    </Badge>
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools text-end">
                    <ul className="nk-tb-actions gx-1">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                            <Icon name="more-h"></Icon>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <ul className="link-list-opt no-bdr">
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#view"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    handleOpenDetailModal(partner);
                                  }}
                                >
                                  <Icon name="eye-fill"></Icon>
                                  <span>View Details</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#edit"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    handleOpenEditModal(partner);
                                  }}
                                >
                                  <Icon name="edit-alt-fill"></Icon>
                                  <span>Edit</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#delete"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    handleOpenDeleteModal(partner);
                                  }}
                                >
                                  <Icon name="trash-fill"></Icon>
                                  <span>Delete</span>
                                </DropdownItem>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              ))}
            </DataTableBody>
            <div className="card-inner">
              {channelPartners.length > 0 && (
                <PaginationComponent
                  itemPerPage={itemsPerPage}
                  totalItems={totalItems}
                  paginate={paginate}
                  currentPage={currentPage}
                  setItemsPerPage={setItemsPerPage}
                  showItemsPerPage={true}
                />
              )}
            </div>
          </DataTable>
        </Block>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={showDeleteConfirmModal} toggle={handleCloseDeleteModal}>
          <ModalHeader toggle={handleCloseDeleteModal}>Confirm Delete</ModalHeader>
          <ModalBody>
            Are you sure you want to delete <strong>{partnerToDelete?.name}</strong>? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={handleDeleteConfirm} disabled={deleteChannelPartnerMutation.isLoading}>
              {deleteChannelPartnerMutation.isLoading ? <Spinner size="sm" /> : "Confirm Delete"}
            </Button>
            <Button color="secondary" onClick={handleCloseDeleteModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <AddChannelPartnerModal
          isOpen={showAddModal}
          toggle={handleCloseAddModal}
          newPartnerData={newPartnerData}
          handleNewPartnerDataChange={handleNewPartnerDataChange}
          handleNewPartnerTypeChange={handleNewPartnerTypeChange}
          typeOptions={typeOptions}
          handleAddPartnerSubmit={handleAddPartnerSubmit}
          isLoading={addChannelPartnerMutation.isLoading}
        />

        {editingPartner && (
          <EditChannelPartnerModal
            isOpen={showEditModal}
            toggle={handleCloseEditModal}
            editingPartner={editingPartner}
            formData={newPartnerData} // Pass newPartnerData as formData
            handleInputChange={handleNewPartnerDataChange} // Pass the correct handler
            handleTypeChange={handleNewPartnerTypeChange} // Pass the correct handler
            typeOptions={typeOptions}
            handleEditPartnerSubmit={handleEditPartnerSubmit}
            isLoading={updateChannelPartnerMutation.isLoading}
          />
        )}

        {viewingPartner && (
          <ChannelPartnerDetailModal
            isOpen={showDetailModal}
            toggle={handleCloseDetailModal}
            partner={viewingPartner}
          />
        )}

      </Content>
    </React.Fragment>

  );
};

export default ChannelPartnerListPage;
